import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { AGE_BANDS, EventPublic } from '../../hooks/event-comparison'
import { SERIES_BASE, SERIES_COMPARE } from './styles'

const INK = '#212121'
const MUTED = '#555555'
const FAINT = '#B0B0B0'
const BORDER = '#EAECF0'
const TRACK = '#EEF1F6'

/* ── layout ── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`
const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`
const Card = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  padding: 16px;
`
const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 660;
  color: ${INK};
`
const CardSub = styled.div`
  font-size: 11.5px;
  color: ${FAINT};
  margin: 2px 0 14px;
`
const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 4px 0 12px;
`
const SectionKicker = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${FAINT};
  text-transform: uppercase;
`
const SectionName = styled.h2`
  font-size: 15px;
  font-weight: 680;
  color: ${INK};
  margin: 0;
`
const SectionNote = styled.span`
  margin-left: auto;
  font-size: 11.5px;
  color: ${FAINT};
`
const Empty = styled.div`
  font-size: 12.5px;
  color: ${FAINT};
  padding: 8px 0;
`

/* ── faixa etária: barras verticais agrupadas ── */
const AgeWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 170px;
  padding-top: 6px;
`
const AgeCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  height: 100%;
`
const AgeBars = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
`
const AgeBar = styled.div<{ $h: number; $c: string }>`
  width: 40%;
  max-width: 16px;
  height: ${(p) => Math.max(p.$h, 1)}%;
  background: ${(p) => p.$c};
  border-radius: 3px 3px 0 0;
`
const AgeLabel = styled.span`
  font-size: 10.5px;
  color: ${FAINT};
  white-space: nowrap;
`

/* ── donuts (sexo / recorrência) ── */
const DonutRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-around;
`
const DonutCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
`
const DonutCap = styled.div`
  text-align: center;
  b {
    display: block;
    font-size: 12px;
    color: ${INK};
    font-weight: 600;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    font-size: 10.5px;
    color: ${FAINT};
  }
`

/* ── barras horizontais (estados / pagamento) ── */
const HRow = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
  & + & {
    border-top: 1px solid #f2f4f7;
  }
`
const HLabel = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${INK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const HBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const HTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const HFill = styled.div<{ $w: number; $c: string }>`
  height: 9px;
  border-radius: 5px;
  background: ${(p) => p.$c};
  width: ${(p) => Math.max(p.$w, 0)}%;
  min-width: 2px;
  transition: width 0.2s;
`
const HPct = styled.span<{ $c: string }>`
  font-size: 12px;
  font-weight: 660;
  color: ${(p) => p.$c};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
const LegendMini = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  font-size: 11.5px;
  color: ${MUTED};
  margin-top: 12px;
`
const LDot = styled.span<{ $c: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$c};
  margin-right: 6px;
`

const pct1 = (n: number) => `${n.toFixed(n < 10 ? 1 : 0)}%`

/** Donut SVG simples: anel com % central e rótulo curto. */
const Donut: FC<{ value: number; color: string; center: string; label: string }> = ({
  value,
  color,
  center,
  label,
}) => {
  const r = 30
  const c = 2 * Math.PI * r
  const dash = (Math.min(Math.max(value, 0), 100) / 100) * c
  return (
    <svg width={78} height={78} viewBox="0 0 78 78">
      <circle cx={39} cy={39} r={r} fill="none" stroke={TRACK} strokeWidth={9} />
      <circle
        cx={39}
        cy={39}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={9}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        transform="rotate(-90 39 39)"
      />
      <text x={39} y={37} textAnchor="middle" fontSize={16} fontWeight={720} fill={INK}>
        {center}
      </text>
      <text x={39} y={50} textAnchor="middle" fontSize={8} fill={FAINT} letterSpacing="0.06em">
        {label}
      </text>
    </svg>
  )
}

interface Props {
  baseName: string
  compareName: string
  basePublic: EventPublic
  comparePublic: EventPublic
  excludeCourtesy: boolean
  courtesyBase: number // % de cortesias do evento base (do timeline)
  courtesyCompare: number
}

/** Seção PÚBLICO — perfil de quem comprou, agregado da janela (LGPD). OP-3414 Fase 2. */
const PublicBuckets: FC<Props> = ({
  baseName,
  compareName,
  basePublic,
  comparePublic,
  excludeCourtesy,
  courtesyBase,
  courtesyCompare,
}) => {
  // faixa etária: bandas fixas, escala pelo maior valor entre os dois eventos
  const ageRows = useMemo(() => {
    const rows = AGE_BANDS.map((band) => ({
      band,
      a: basePublic.agesMap[band] || 0,
      b: comparePublic.agesMap[band] || 0,
    }))
    const max = Math.max(1, ...rows.map((r) => Math.max(r.a, r.b)))
    return { rows, max }
  }, [basePublic.agesMap, comparePublic.agesMap])

  // pagamentos: união das categorias, ordenado pelo base, top 6
  const payRows = useMemo(
    () => alignRows(basePublic.paymentsMap, comparePublic.paymentsMap, 6),
    [basePublic.paymentsMap, comparePublic.paymentsMap]
  )
  // estados: união das UFs, top 6
  const stateRows = useMemo(
    () => alignRows(basePublic.statesMap, comparePublic.statesMap, 6),
    [basePublic.statesMap, comparePublic.statesMap]
  )
  // cidades: união das cidades, top 6
  const cityRows = useMemo(
    () => alignRows(basePublic.citiesMap, comparePublic.citiesMap, 6),
    [basePublic.citiesMap, comparePublic.citiesMap]
  )

  const rec = (p: EventPublic) =>
    p.recurrence ? (excludeCourtesy ? p.recurrence.semCortesia : p.recurrence.comCortesia) : null
  const recA = rec(basePublic)
  const recB = rec(comparePublic)

  const anyLoading = basePublic.loading || comparePublic.loading

  return (
    <>
      <SectionHead>
        <SectionKicker>Público</SectionKicker>
        <SectionName>Perfil de quem comprou</SectionName>
        <SectionNote>Agregado da janela — dados anônimos (LGPD)</SectionNote>
      </SectionHead>

      <Grid>
        {/* Faixa etária */}
        <Card>
          <CardTitle>Faixa etária</CardTitle>
          <CardSub>Distribuição % · Base × Comparado</CardSub>
          {ageRows.rows.every((r) => r.a === 0 && r.b === 0) ? (
            <Empty>{anyLoading ? 'Carregando…' : 'Sem dados de idade na janela.'}</Empty>
          ) : (
            <AgeWrap>
              {ageRows.rows.map((r) => (
                <AgeCol key={r.band}>
                  <AgeBars>
                    <AgeBar $h={(r.a / ageRows.max) * 100} $c={SERIES_BASE} title={pct1(r.a)} />
                    <AgeBar $h={(r.b / ageRows.max) * 100} $c={SERIES_COMPARE} title={pct1(r.b)} />
                  </AgeBars>
                  <AgeLabel>{r.band}</AgeLabel>
                </AgeCol>
              ))}
            </AgeWrap>
          )}
        </Card>

        {/* Sexo */}
        <Card>
          <CardTitle>Sexo</CardTitle>
          <CardSub>Participação por evento</CardSub>
          {!basePublic.gender && !comparePublic.gender ? (
            <Empty>{anyLoading ? 'Carregando…' : 'Sem dados de sexo na janela.'}</Empty>
          ) : (
            <DonutRow>
              <GenderDonut name={baseName} g={basePublic.gender} color={SERIES_BASE} />
              <GenderDonut name={compareName} g={comparePublic.gender} color={SERIES_COMPARE} />
            </DonutRow>
          )}
        </Card>

        {/* Recorrência */}
        <Card>
          <CardTitle>Recorrência de compra</CardTitle>
          <CardSub>Novos × recorrentes do produtor</CardSub>
          {!recA && !recB ? (
            <Empty>{anyLoading ? 'Carregando…' : 'Sem dados de recorrência na janela.'}</Empty>
          ) : (
            <DonutRow>
              <DonutCol>
                <Donut
                  value={recA?.recorrentesPct || 0}
                  color={SERIES_BASE}
                  center={`${Math.round(recA?.recorrentesPct || 0)}%`}
                  label="RECORR."
                />
                <DonutCap>
                  <b>{baseName}</b>
                  <span>
                    Novos {Math.round(recA?.novosPct || 0)}% · Recorr. {Math.round(recA?.recorrentesPct || 0)}%
                  </span>
                </DonutCap>
              </DonutCol>
              <DonutCol>
                <Donut
                  value={recB?.recorrentesPct || 0}
                  color={SERIES_COMPARE}
                  center={`${Math.round(recB?.recorrentesPct || 0)}%`}
                  label="RECORR."
                />
                <DonutCap>
                  <b>{compareName}</b>
                  <span>
                    Novos {Math.round(recB?.novosPct || 0)}% · Recorr. {Math.round(recB?.recorrentesPct || 0)}%
                  </span>
                </DonutCap>
              </DonutCol>
            </DonutRow>
          )}
        </Card>
      </Grid>

      <Grid2>
        {/* Top estados */}
        <Card>
          <CardTitle>Top estados</CardTitle>
          <CardSub>% dos ingressos por UF · sobre vendas com estado informado</CardSub>
          <HBarList rows={stateRows} />
          <LegendMini>
            <span><LDot $c={SERIES_BASE} />{baseName}</span>
            <span><LDot $c={SERIES_COMPARE} />{compareName}</span>
          </LegendMini>
        </Card>

        {/* Top cidades */}
        <Card>
          <CardTitle>Top cidades</CardTitle>
          <CardSub>% dos ingressos por cidade · sobre vendas com localização informada</CardSub>
          <HBarList rows={cityRows} />
          <LegendMini>
            <span><LDot $c={SERIES_BASE} />{baseName}</span>
            <span><LDot $c={SERIES_COMPARE} />{compareName}</span>
          </LegendMini>
        </Card>
      </Grid2>

      <Grid2>
        {/* Formas de pagamento */}
        <Card>
          <CardTitle>Formas de pagamento</CardTitle>
          <CardSub>% dos ingressos · Base × Comparado</CardSub>
          <HBarList rows={payRows} />
          <LegendMini>
            <span><LDot $c={SERIES_BASE} />{baseName}</span>
            <span><LDot $c={SERIES_COMPARE} />{compareName}</span>
          </LegendMini>
        </Card>

        {/* Peso de cortesias */}
        <Card>
          <CardTitle>Peso de cortesias</CardTitle>
          <CardSub>% de ingressos com valor zero na janela</CardSub>
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <CortesiaStat name={baseName} pct={courtesyBase} color={SERIES_BASE} />
            <CortesiaStat name={compareName} pct={courtesyCompare} color={SERIES_COMPARE} />
          </div>
        </Card>
      </Grid2>
    </>
  )
}

const CortesiaStat: FC<{ name: string; pct: number; color: string }> = ({ name, pct, color }) => (
  <div style={{ flex: 1, background: '#F9F9F9', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14 }}>
    <div style={{ fontSize: 11, color: MUTED, fontWeight: 560, display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 9, height: 9, borderRadius: 3, background: color, display: 'inline-block' }} /> {name}
    </div>
    <div style={{ fontFamily: 'inherit', fontSize: 24, fontWeight: 720, color: INK, marginTop: 8 }}>
      {pct.toFixed(1).replace('.', ',')}%
    </div>
  </div>
)

/* donut de sexo com legenda M/F/O */
const GenderDonut: FC<{ name: string; g: EventPublic['gender']; color: string }> = ({
  name,
  g,
  color,
}) => (
  <DonutCol>
    <Donut value={g?.f || 0} color={color} center={`${Math.round(g?.f || 0)}%`} label="FEM." />
    <DonutCap>
      <b>{name}</b>
      <span>
        M {Math.round(g?.m || 0)}% · F {Math.round(g?.f || 0)}% · O {Math.round(g?.o || 0)}%
      </span>
    </DonutCap>
  </DonutCol>
)

interface AlignedRow {
  key: string
  a: number
  b: number
}
/** União de chaves de dois mapas de %, ordenada pelo valor base (desc), top N. */
function alignRows(
  mapA: Record<string, number>,
  mapB: Record<string, number>,
  top: number
): { rows: AlignedRow[]; max: number } {
  const keys = Array.from(new Set([...Object.keys(mapA), ...Object.keys(mapB)]))
  const rows = keys
    .map((key) => ({ key, a: mapA[key] || 0, b: mapB[key] || 0 }))
    .sort((x, y) => Math.max(y.a, y.b) - Math.max(x.a, x.b))
    .slice(0, top)
  const max = Math.max(1, ...rows.map((r) => Math.max(r.a, r.b)))
  return { rows, max }
}

const HBarList: FC<{ rows: { rows: AlignedRow[]; max: number } }> = ({ rows }) => {
  if (rows.rows.length === 0) return <Empty>Sem dados na janela.</Empty>
  return (
    <div>
      {rows.rows.map((r) => (
        <HRow key={r.key}>
          <HLabel>{r.key}</HLabel>
          <HBars>
            <HTrack>
              <HFill $w={(r.a / rows.max) * 100} $c={SERIES_BASE} />
              <HPct $c={SERIES_BASE}>{pct1(r.a)}</HPct>
            </HTrack>
            <HTrack>
              <HFill $w={(r.b / rows.max) * 100} $c={SERIES_COMPARE} />
              <HPct $c={SERIES_COMPARE}>{pct1(r.b)}</HPct>
            </HTrack>
          </HBars>
        </HRow>
      ))}
    </div>
  )
}

export default PublicBuckets
